import { get, map, keyBy, isEmpty, pick, chain, compact, size, toLower, includes } from "lodash"
import { numberFormatter, percentageFormatter, scaledPercentageFormatter, makeCurrencyFormatter } from "helpers/formatters/numbers"
import { LONG_SHORT, OVERWEIGHT_UNDERWEIGHT } from "resources/position-sets/composition"
import { getQueryGroupBy } from "helpers/rollups"
import { getTickerAndMicFromContributor, isUnmodeled, formatLabel } from "helpers/securities"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { DEFAULT_CURRENCY } from "helpers/formatters/numbers"
import { getBetaInstrumentLabelAndTooltip } from "helpers/contributors"
import { GMV, REFERENCE_EQUITY, MODEL_COVERAGE, POSITIONS, CONCENTRATION, MAX_CONCENTRATION, MIN_CONCENTRATION, LONG, SHORT, OVERWEIGHT, UNDERWEIGHT, NET, MARKET_CAP, PREDICTED_BETA, HISTORICAL_BETA, CUSTOM_BETA } from "../constants"
import { getBetaInstrumentFromOptions, sanitizePositionSet } from "../helpers"
import { getMetricValue } from "../composition-helpers"

const DYNAMIC_PREDICTED_BETA_FIELD = "dynamicPredicted: predicted"
const BETA_INSTRUMENT_PARAMETER = "instrument: $instrument"

export const getMetrics = (currency) => {
  const currencyFormatter = makeCurrencyFormatter(currency)
  return {
    [GMV]: {
      label: "GMV",
      valueGetter: data => get(data, "data.model.simulation.composition.0.gmv"),
      formatter: currencyFormatter,
    },
    [REFERENCE_EQUITY]: {
      label: "Reference Equity",
      valueGetter: data => get(data, "data.model.simulation.composition.0.referenceEquity", null),
      formatter: currencyFormatter,
    },
    [MODEL_COVERAGE]: {
      label: "Model Coverage",
      valueGetter: data => get(data, "data.model.simulation.coverage.0.summary.percentGmvAvailable"),
      formatter: percentageFormatter,
    },
    [POSITIONS]: {
      label: "Positions",
      valueGetter: data => get(data, "data.model.simulation.composition.0.positionsCount"),
      formatter: (nbPositions) => nbPositions,
    },
    [MAX_CONCENTRATION]: {
      label: "Max Concentration",
      valueGetter: data => get(data, "data.model.simulation.composition.0.concentration.max"),
    },
    [MIN_CONCENTRATION]: {
      label: "Min Concentration",
      valueGetter: data => get(data, "data.model.simulation.composition.0.concentration.min"),
    },
    [CONCENTRATION]: {
      label: "Concentration",
      valueGetter: data => get(data, "data.model.simulation.composition.0.concentration"),
      formatter: (concentration) => `${numberFormatter(concentration?.min)} - ${scaledPercentageFormatter(concentration?.max)}`,
    },
    [LONG]: {
      label: "Long",
      valueGetter: data => get(data, "data.model.simulation.composition.0.long"),
    },
    [SHORT]: {
      label: "Short",
      valueGetter: data => get(data, "data.model.simulation.composition.0.short"),
    },
    [OVERWEIGHT]: {
      label: "Overweight",
      valueGetter: data => get(data, "data.model.simulation.composition.0.overweight"),
    },
    [UNDERWEIGHT]: {
      label: "Underweight",
      valueGetter: data => get(data, "data.model.simulation.composition.0.underweight"),
    },
    [NET]: {
      label: "Net",
      valueGetter: data => get(data, "data.model.simulation.composition.0.net"),
    },
    [MARKET_CAP]: {
      label: "Market Capitalization",
      valueGetter: data => get(data, "data.model.simulation.composition.0.summaryStats.average.marketCapitalization"),
      formatter: currencyFormatter,
    },
  }
}

const getBetaMetrics = (modelName, betaInstrument) => {
  const betaMetrics = {
    [PREDICTED_BETA]: {
      label: "Predicted Beta",
      valueGetter: data => get(data, "data.model.simulation.beta.0.predicted"),
      tooltip: `Predicted Beta values are provided by the ${modelName} model.`,
      formatter: numberFormatter,
    },
    [HISTORICAL_BETA]: {
      label: "Historical Beta",
      valueGetter: data => get(data, "data.model.simulation.beta.0.historical"),
      tooltip: `Historical Beta values are provided by the ${modelName} model.`,
      formatter: numberFormatter,
    },
  }
  if (betaInstrument) {
    const { label, tooltipContents } = getBetaInstrumentLabelAndTooltip(betaInstrument)
    betaMetrics[CUSTOM_BETA] = {
      label,
      valueGetter: data => get(data, "data.model.simulation.custom.0.dynamicPredicted"),
      formatter: numberFormatter,
      tooltip: tooltipContents,
    }
  }
  return betaMetrics
}

const getMetricsQuery = (metrics, betaInstrument, shouldUseIntradayQuery) => {
  const extraParameters = compact([
    betaInstrument && ", $instrument: PositionSetInput",
  ]).join(", ")

  const innerQuery = `
      coverage {
        date
        summary {
          percentGmvAvailable
        }
      }
      longShort: composition {
        date
        longShortComposition: compositionBy(groupBy: LONG_SHORT) {
          id
        }
      }
      composition(scaleFormat: PERCENT_EQUITY_GMV) {
        date
        gmv
        referenceEquity
        positionsCount
        modeledGmv
        summaryStats {
          average {
            marketCapitalization
          }
        }
        concentration {
          min
          max
        }
      }
      beta{
        date
        ${includes(metrics, PREDICTED_BETA) ? "predicted" : ""}
        ${includes(metrics, HISTORICAL_BETA) ? "historical" : ""}
      }
      ${betaInstrument ? `custom: beta(${BETA_INSTRUMENT_PARAMETER}) {
        ${DYNAMIC_PREDICTED_BETA_FIELD}
      }` : ""}
  `

  if (shouldUseIntradayQuery) {
    return `query compareCompositionPositionSet($modelId: String!, $positionSet: PositionSetInput!, $on: Date!, $batchId: Id) {
      model(id: $modelId) {
        simulation:intradaySimulationBatch(
          positionSet: $positionSet
          on: $on
          batchId: $batchId
        ) {
          ${innerQuery}
       }
     }
    }`
  }

  return `query compareCompositionPositionSet($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date! ${extraParameters}) {
    model(id: $modelId) {
      simulation(
        positionSet: $positionSet
        base: $base
        from: $on
        to: $on
        interval: AUTO
      ) {
        ${innerQuery}
      }
    }
  }`
}

// Classifications is needed to display the positions hover tooltips correctly
const getPositionsQuery = (classifications, shouldUseIntradayQuery, group) => {
  const groupByQuery = group ? `compositionBy(groupBy: $groupBy, groupById: $groupById, classificationTier: $tier){
    id
    label:description
    economicExposure
  }` : ""
  const groupByVariables = group ? "$groupBy:ContributorGroupType!, $groupById:String, $tier:String" : ""

  const innerQuery = `composition(scaleFormat: PERCENT_EQUITY_GMV) {
        positions {
          id
          assetClass
          economicExposure
          basePercentEquity: baseEconomicExposure
          positionPercentEquity: positionSetEconomicExposure
          description
          isUnmodeled
          currency
          country
          modelProviderId
          ${generateClassificationQueryForContributors("GICS", classifications)}
        }
        ${groupByQuery}
      }`

  if (shouldUseIntradayQuery) {
    return `query ($modelId: String!, $positionSet: PositionSetInput!, $on: Date!, $batchId: Id, ${groupByVariables}) {
        model(id: $modelId) {
          simulation:intradaySimulationBatch(
            positionSet: $positionSet
            on: $on
            batchId: $batchId
          ) {
            ${innerQuery}
          }
        }
      }`
  }

  return `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, ${groupByVariables}) {
  model(id: $modelId) {
    simulation(
      positionSet: $positionSet
      base: $base
      from: $on
      to: $on
      interval: AUTO
    ) {
      ${innerQuery}
    }
  }
}`
}

function selectMetrics(graphqlResult, metrics, noGmvOrEquity, currency, modelName, betaInstrument, hasReferenceEquity) {
  const allMetrics = {
    ...getMetrics(currency),
    ...getBetaMetrics(modelName, betaInstrument),
  }

  const values = map(metrics, (metric) => ({
    ...allMetrics[metric],
    id: metric,
    value: getMetricValue(metric, allMetrics, graphqlResult, noGmvOrEquity, hasReferenceEquity),
  }))

  return {
    values: keyBy(values, "id"),
  }
}

function selectPositions(graphqlResult) {
  const positions = get(graphqlResult, "data.model.simulation.composition.0.positions", [])
  const values = map(positions, (position) => {
    const tickerMic = getTickerAndMicFromContributor(position)
    const newPosition = {
      ...position,
      ...tickerMic,
      isUnmodeled: isUnmodeled(position),
    }
    newPosition.label = formatLabel(newPosition)
    return newPosition
  })

  return {
    values: keyBy(values, "id"),
  }
}

function makeNetGetter(group) {
  const valueGetter = (metricId) => (compositionBy) => {
    return chain(compositionBy)
      .find(({ id }) => toLower(id) === toLower(metricId))
      .get("economicExposure", null)
      .value()
  }

  if (group === OVERWEIGHT_UNDERWEIGHT) {
    return (compositionBy) => {
      return Math.abs(valueGetter(OVERWEIGHT)(compositionBy)) - Math.abs(valueGetter(UNDERWEIGHT)(compositionBy))
    }
  }
  if (group === LONG_SHORT) {
    return (compositionBy) => {
      return Math.abs(valueGetter(LONG)(compositionBy)) - Math.abs(valueGetter(SHORT)(compositionBy))
    }
  }
  return null
}

function selectGroups(graphqlResult, group, hasNet) {
  const compositionBy = get(graphqlResult, "data.model.simulation.composition.0.compositionBy", [])
  const netGetter = makeNetGetter(group.groupBy)
  const values = compact([
    ...compositionBy,
    hasNet && size(compositionBy) === 2 && {
      id: NET,
      label: "Net",
      economicExposure: netGetter(compositionBy),
      notSummable: true,
    },
  ])

  return {
    values: keyBy(values, "id"),
  }
}

export function createCompositionSingleDateQuery(model, positionSet, base, date, options = {}) {
  const { id: modelId, name: modelName } = model
  const isActive = !isEmpty(base)

  const noGmvOrEquity = options?.noGmvOrEquity || false
  const currency = options?.currency || DEFAULT_CURRENCY
  const type = options?.type || "metrics"
  const metrics = options?.metrics || []
  const group = options?.groupByOption ? getQueryGroupBy(options.groupByOption, isActive) : null
  const hasNet = group?.groupBy === OVERWEIGHT_UNDERWEIGHT || group?.groupBy === LONG_SHORT
  const classifications = options?.classifications || []
  const betaInstrument = getBetaInstrumentFromOptions(options)
  const hasReferenceEquity = options?.hasReferenceEquity || false
  const { batchId, isIntradayMode, intradayEnabled } = options
  const shouldUseIntradayQuery = isIntradayMode && !!intradayEnabled

  return {
    key: ["composition", "singleDate", modelId, positionSet, base, date, type, group, metrics, shouldUseIntradayQuery, batchId],
    query: type === "metrics" ? getMetricsQuery(metrics, betaInstrument, shouldUseIntradayQuery) : getPositionsQuery(classifications, shouldUseIntradayQuery, group),
    variables: {
      modelId,
      positionSet: {
        ...sanitizePositionSet(positionSet),
        currency,
      },
      base,
      on: date,
      batchId,
      instrument: betaInstrument,
      ...pick(group, ["groupBy", "groupById", "tier"]),
    },
    select: (graphqlResult) => {
      switch (type) {
        case "positions":
          return selectPositions(graphqlResult)
        case "groups":
          return selectGroups(graphqlResult, group, hasNet)
        case "metrics":
        default:
          // Pass options.betaInstrument to use the full beta instrument object
          return selectMetrics(graphqlResult, metrics, noGmvOrEquity, currency, modelName, options.betaInstrument, hasReferenceEquity)
      }
    },
  }
}
